/**
 * The network and reload side of stale-build detection, used by the router-level VersionGuard.
 * The pure decisions live in ./buildVersion; see there for why this exists.
 */

import { setBypassNavigationGuard } from '../hooks/useNavigationGuard';
import {
  RUNNING_BUILD_ID,
  isStaleBuild,
  parseDeployedBuildId,
  readLastBuildReload,
  recordBuildReload,
  shouldReloadForNewBuild,
} from './buildVersion';

/** Set when a background check (tab shown again) found a newer build on the server. */
let staleBuildSeen = false;

/** Whether an earlier background check found a newer build on the server. */
export function hasSeenStaleBuild(): boolean {
  return staleBuildSeen;
}

/** Remember that a newer build is on the server, for the next navigation to act on. */
export function markStaleBuildSeen(): void {
  staleBuildSeen = true;
}

/** For tests only: forget a stale build seen by an earlier check. */
export function resetStaleBuildForTests(): void {
  staleBuildSeen = false;
}

/** Whether the check runs at all: off where no build id was injected (Jest). */
export function isBuildCheckEnabled(): boolean {
  return RUNNING_BUILD_ID !== '';
}

/**
 * Ask the server which build it holds. Resolves true only when it answers with a build id that
 * differs from the running one; any failure (offline, no version.json, not JSON) resolves false.
 * A bare `fetch`, not the API client, so it never touches the API interceptors.
 */
export function checkForNewBuild(): Promise<boolean> {
  return fetch(`${window.location.origin}/version.json?t=${Date.now()}`, { cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : null))
    .then((body) => isStaleBuild(RUNNING_BUILD_ID, parseDeployedBuildId(body)))
    .catch(() => false);
}

/** Reload onto the new build, at most once per 30s (see shouldReloadForNewBuild). */
export function reloadOntoNewBuild(): void {
  const now = Date.now();
  if (!shouldReloadForNewBuild(readLastBuildReload(), now)) {
    return;
  }
  recordBuildReload(now);
  // Suppress the "Leave site?" guard, as the maintenance reload does (FR-171): the user has
  // already chosen to navigate, and the reload lands them on the same address.
  setBypassNavigationGuard(true);
  window.location.reload();
}
